import React from 'react';
import { useTranslation } from 'react-i18next';
import { OrderEvent, useGetOrderEventsQuery } from '../../../api/booking/orderEventsApiSlice';
import { formatDateForDisplay } from '../../../utils';

// Event-log timeline for a booking (W2.1, docs/plans/event-log-w21-handoff-
// spec.md §6). Renders the append-only history behind the LockerAccessLog
// summary line: every event the backend + monitoring cron recorded for this
// order, occurred_at ascending.
//
// Reader-tolerance rule (codified after the 2026-07-18 whitescreen): unknown
// `eventType`s render generically with the raw registry name — producers may
// ship new events before this UI knows them. Readers degrade, never crash.

// Registry names this UI knows how to label (spec §3). Anything else falls
// back to the raw eventType string.
const KNOWN_EVENT_LABEL_KEYS: Record<string, string> = {
    'usage.locker.open_commanded': 'booking_details.activity_timeline.open_commanded',
    'usage.locker.open_confirmed': 'booking_details.activity_timeline.open_confirmed',
    'usage.locker.close_confirmed': 'booking_details.activity_timeline.close_confirmed',
    'booking.created': 'booking_details.activity_timeline.booking_created',
    'booking.cancelled': 'booking_details.activity_timeline.booking_cancelled',
};

const eventDoor = (event: OrderEvent): string | null => {
    const door = event.payload?.door;
    return typeof door === 'number' || typeof door === 'string' ? String(door) : null;
};

/** `timeZone`: the booking location's zone (#3219); the station default when omitted. */
const ActivityTimeline = ({ orderId, timeZone }: { orderId: string, timeZone?: string }) => {
    const { t } = useTranslation();
    const { data } = useGetOrderEventsQuery({ orderId }, { skip: !orderId });

    const events = data?.events ?? [];
    if (!events.length) {
        // No history yet (or endpoint unavailable) — the section stays hidden,
        // matching the LockerAccessLog empty behavior.
        return null;
    }

    return (
        <div className={'bg-text-contrast rounded-[5px] shadow-sm p-4 mt-5'}>
            <h2 className={'text-sm font-semibold text-foreground mb-2'}>
                {t('booking_details.activity_timeline.title')}
            </h2>
            <table className={'w-full text-sm'}>
                <tbody>
                    {events.map((event) => {
                        const labelKey = KNOWN_EVENT_LABEL_KEYS[event.eventType];
                        const door = eventDoor(event);
                        return (
                            <tr key={event.id} className={'border-t border-foreground/5 first:border-t-0'}>
                                <td className={'py-1.5 pr-4 whitespace-nowrap text-foreground/60 align-top'}>
                                    {formatDateForDisplay(event.occurredAt, timeZone)}
                                </td>
                                <td className={'py-1.5 text-foreground'}>
                                    {labelKey ? t(labelKey) : event.eventType}
                                    {door && (
                                        <span className={'text-foreground/60'}>
                                            {' · '}
                                            {t('booking_details.locker_log.locker', { door })}
                                        </span>
                                    )}
                                    {event.actor.display && (
                                        <span className={'text-foreground/60'}>
                                            {' · '}
                                            {event.actor.display}
                                        </span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default ActivityTimeline;
